import React, { useEffect, useMemo, useRef, useState } from 'react';
import T from 'prop-types';
// recompose
import { compose } from 'recompose';
import withCorrection from './utils/withCorrectionHOC';
// redux
import { connect } from 'react-redux';
import { reportTreatments, uploadAssetComplete, deleteAsset } from 'reducers/dailyCheckup/checkupEdit';
import { openViewer } from 'reducers/mediaViewer';
import { openPortalRight, closePortalRight } from 'reducers/rightToLeftPortal';
import {
  fetchList as fetchTreatmentsList,
  setDestroyedData,
  setSelectedData,
  selectItem as selectTreatment,
  resetData as resetTreatmentsData,
} from 'reducers/mobileListPicker';
import { setPageOptions } from 'reducers/layout';
// components
import { FormattedMessage } from 'react-intl';
import AttentionBox from 'components/AttentionBox/AttentionBox';
import Button from 'components/Button';
import CenterBox from 'components/CenterBox';
import CommentBox from 'components/CommentBox';
import DailyCheckupEditProtector from './DailyCheckupEditProtector';
import Link from 'react-router/lib/Link';
import MediaUploader from 'components/MediaUploader/MediaUploader';
import MessageBox from 'components/MessageBox';
import MobileListPickerAllowDuplicates from 'components/MobileListPicker/MobileListPickerAllowDuplicates';
import MobileListPicker from 'components/MobileListPicker/MobileListPicker';
import MobileTreatmentRow from 'components/DailyCheckups/MobileTreatmentRow';
import MobileTreatmentRowWithCounter from 'components/DailyCheckups/MobileTreatmentRowWithCounter';
import NumberStepInput from 'components/NumberStepInput/NumberStepInput';
import OralTreatmentUnit from 'components/DailyCheckups/TreatmentProductLabels/OralTreatmentUnit';
import PigsUnderCareLine from 'components/PigsUnderCareLine/PigsUnderCareLine';
import ReactSelect from 'components/Select/ReactSelect';
import StickyFooter from 'components/StickyFooter/StickyFooter';
import Translation from 'components/Translation';
import TreatmentProtocolsProductSteps from './components/TreatmentProtocolsProductSteps';
import WithdrawalWarningBox from './components/WithdrawalWarningBox';
import PigsUnderTreatmentLine from 'components/PigsUnderTreatmentLine';
import ButtonRadioGroup from 'components/ButtonRadioGroup/ButtonRadioGroup';
// utils
import { animateScroll } from 'react-scroll';
import { getCheckupBackUrls } from 'utils/checkupHelper';
import { getCommentByRole } from 'helpers.js';
import { getMedType, withdrawalParams, formatDosageType, stockSolutionToSystem, stockSolutionToStored } from 'utils';
import { isMobile } from 'react-device-detect';
import { sendNotify } from 'utils/airbrakeHelper';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import cn from 'classnames';
import isEmpty from 'lodash.isempty';
import moment from 'moment';
import update from 'react-addons-update';
// assets
import './DailyCheckupReportTreatments.scss';
// constants
import { comment_roles, API_URL } from 'constants.js';
// endpoints
import { getPigGroupTreatmentProtocolProductSteps } from 'endpoints/treatmentProtocols';

const buildHeadsCountData = (treatments) => treatments
  .filter(({ treatment_product_id, _destroy }) => treatment_product_id && !_destroy)
  .map(({ head_count }) => head_count)
  .sort((curr, next) => (curr - next));

const calcTotalHeadCount = (treatmentsHeads) => {
  if (!treatmentsHeads.length) return 0;
  return Math.max(...treatmentsHeads);
};

const useTreatmentOptions = [
  { label: <FormattedMessage id="general.button.yes" />, value: true },
  { label: <FormattedMessage id="general.button.no" />, value: false },
];

const newTreatmentOptions = [
  { label: <FormattedMessage id="general.button.firstTreatment" />, value: true },
  { label: <FormattedMessage id="general.button.retreatment" />, value: false },
];

const DailyCheckupReportTreatments = ({
  checkup,
  closePortalRight,
  currentUser,
  deleteAsset,
  destroyedTreatments,
  fetchTreatmentsList,
  isEditMode,
  isOnline,
  isReportingTreatments,
  location,
  makeCheckupCorrection,
  measurementSystem,
  openPortalRight,
  openViewer,
  params,
  reportTreatments,
  resetTreatmentsData,
  selectedTreatments,
  selectTreatment,
  setDestroyedData,
  setPageOptions,
  setSelectedData,
  treatmentsList,
  uploadAssetComplete,
  validateCheckup,
  withdrawalTrackingOn,
  hasMentions,
}, { router }) => {
  const [comment, setComment] = useState('');
  const [mentionsIds, setMentionsIds] = useState([]);
  const [headCount, setHeadCount] = useState(0);
  const [selectedTreatmentIndex, setSelectedTreatmentIndex] = useState();
  const { media_upload } = currentUser.current_company;
  const isAdmin = currentUser.roles_map.admin;
  const [futureSteps, setFutureSteps] = useState([]);

  const setInitialData = () => {
    if (isEmpty(checkup)) {
      return;
    }

    const { TREATMENTS_ROLE } = comment_roles;
    const { comment, mentioned_user_ids } = getCommentByRole(checkup.comments || [], TREATMENTS_ROLE);
    const { prevPageUrl } = getCheckupBackUrls(checkup, location);

    setComment(comment || '');
    setMentionsIds(mentioned_user_ids || []);
    setHeadCount(checkup.total_pigs_treated);

    setPageOptions({
      mobileTitle: 'reportMedications',
      backLink: { link: prevPageUrl, icon: 'fa fa-times fs-18' },
      inEditMode: isEditMode,
    });

    if (!checkup.dc_treatments.length) {
      setSelectedData(isMobile ? [] : [{}]);
      if (isMobile) {
        openPortalRight(
          <MobileListPickerAllowDuplicates
            title={<FormattedMessage id="general.pageTitle.addMedications" />}
            mode="add"
            idKey="treatment_product_id"
            rightButtonOptions={{ label: 'next', disableOnEmptyList: true }}
            // eslint-disable-next-line no-use-before-define
            rowRenderer={renderMobileListRow()}
          />
        );
      }
    } else {
      const dcTreatments = checkup.dc_treatments.map((treatment) => ({
        ...treatment,
        quantity: parseFloat(treatment.quantity).toFixed(2) || null,
        gallons_value: stockSolutionToSystem(parseFloat(treatment.gallons_value) || null, measurementSystem),
      }));
      setSelectedData(dcTreatments);
    }
  };

  // Future-step matching is a same-day concept; a correction links steps through the dated panel
  useEffect(() => {
    if (checkup.pig_group?.id && !isEditMode) {
      getPigGroupTreatmentProtocolProductSteps(checkup.pig_group.id, { without_used: true })
        .then((response) => {
          setFutureSteps(response.resources || []);
        })
        .catch((response) => {
          toastResponseErrors(response);
        });
    }
  }, [checkup.pig_group?.id, isEditMode]);

  useEffect(() => {
    fetchTreatmentsList('/treatment_products');

    return () => {
      resetTreatmentsData();
      setPageOptions({ mobileTitle: '' });
    };
  }, []);

  useEffect(() => {
    setInitialData();
  }, [checkup.id]);

  // Track whether we've already applied the initial selected treatments to avoid recalculating on page load
  const useRefSelectedInitialized = useRef(false);

  useEffect(() => {
    // Skip auto-recalc on the first load to preserve existing total from the backend
    if (!useRefSelectedInitialized.current) {
      useRefSelectedInitialized.current = true;
      return;
    }
    const treatmentsHeadsArr = buildHeadsCountData(selectedTreatments.filter((item) => item.useTreatment !== false));
    setHeadCount(calcTotalHeadCount(treatmentsHeadsArr));
  }, [selectedTreatments]);

  useEffect(() => {
    useRefSelectedInitialized.current = false;
  }, [checkup.id]);

  // With multi-placement protocols several instances can carry the same product; prefer the step
  // the due-today list surfaces: an already-open window first, then earliest start date, then id
  const findMatchingFutureStep = (productId) => {
    const matching = futureSteps.filter(
      (step) => step.treatment_protocol_product.treatment_product.id === productId
    );
    const today = moment().format('YYYY-MM-DD');
    const isPending = (step) => (step.start_date && step.start_date <= today ? 0 : 1);
    return matching.sort((a, b) => {
      if (isPending(a) !== isPending(b)) return isPending(a) - isPending(b);
      if ((a.start_date || '') !== (b.start_date || '')) return (a.start_date || '') < (b.start_date || '') ? -1 : 1;
      return a.id - b.id;
    })[0];
  };

  const onMobileTreatmentChange = (treatment, selectedTreatmentIndex) => {
    const destroyed = { ...selectedTreatments[selectedTreatmentIndex], _destroy: true };
    setDestroyedData([...destroyedTreatments, destroyed]);

    // Check for matching future step
    const matchingFutureStep = findMatchingFutureStep(parseInt(treatment.id, 10));

    const overrideAttrs = {
      treatment_product_id: treatment.id,
      treatment_product: treatment,
      head_count:
        treatment.vaccine || treatment.route.includes('oral')
          ? checkup?.pigs || 0
          : 0,
      quantity: 0,
      skip_efficacy_survey: false,
      ...(checkup.abf_tracking && !treatment.safe && { new_treatment: true })
    };

    if (matchingFutureStep) {
      overrideAttrs.future_step = matchingFutureStep;
    }

    const treatments = update(selectedTreatments, {
      [selectedTreatmentIndex]: {
        $set: overrideAttrs,
      },
    });
    setSelectedData(treatments);
    closePortalRight();
  };

  const onMobileTreatmentAdd = (treatmentProduct) => {
    const { id, route, vaccine, dosage_measure, dosage_type, safe } = treatmentProduct;
    const initialHeadCount = dosage_measure === 'head' && dosage_type === 'ml' ? 1 : checkup?.pigs || 0;
    let headCount = 0;

    if (checkup.abf_tracking && !safe) {
      headCount = 0;
    } else if (vaccine || route.includes('oral')) {
      headCount = initialHeadCount;
    } else {
      headCount = 0;
    }
    const overrideAttrs = {
      treatment_product: treatmentProduct,
      quantity: 0.00,
      head_count: headCount,
      ...(checkup.abf_tracking && !safe && { new_treatment: true })
    };

    // checking if treatment with added product was previosly destroyed, if it was - mark it as undestroyed and use its values for added one
    const destroyedTreatment = destroyedTreatments.find(({ treatment_product_id }) => treatment_product_id === id);
    const isTreatmentPreviouslyDestroyed = !!destroyedTreatment;
    const addedTreatment = isTreatmentPreviouslyDestroyed
      ? { ...destroyedTreatment, _destroy: undefined }
      : { treatment_product_id: id, skip_efficacy_survey: false };

    // Check for matching future step
    const matchingFutureStep = futureSteps.find(
      (step) => step.treatment_protocol_product.treatment_product.id === parseInt(id, 10)
    );
    if (matchingFutureStep) {
      addedTreatment.future_step = matchingFutureStep;
    }

    selectTreatment({ ...addedTreatment, ...overrideAttrs });

    // if treatment with added product was previosly destroyed - remove it from destroyed list
    if (isTreatmentPreviouslyDestroyed) {
      setDestroyedData(destroyedTreatments
        .filter(({ treatment_product_id }) => (treatment_product_id !== destroyedTreatment.treatment_product_id)));
    }
  };

  const onMobileTreatmentRemove = (treatmentProductId, currentSelected) => {
    // Remove the last added duplicate (only those added in current session without persisted id)
    const list = currentSelected || selectedTreatments;
    let indexToRemove;
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const item = list[i];
      if (item) {
        const sameProduct = Number(item.treatment_product_id) === Number(treatmentProductId);
        if (sameProduct && !item.id && !item._destroy) {
          indexToRemove = i;
          break;
        }
      }
    }
    if (indexToRemove !== undefined) {
      setSelectedData(update(list, { $splice: [[indexToRemove, 1]] }));
    }
  };

  const renderMobileListRow = (selectedIndex) => (treatment, mode, { isSelected, isDisabled, selectedData }) => {
    const onRowClick = {
      change: () => onMobileTreatmentChange(treatment, selectedIndex),
      add: () => onMobileTreatmentAdd(treatment),
      select: () => onMobileTreatmentAdd(treatment),
    }[mode];

    if (mode === 'add') {
      const currentSelected = selectedData || selectedTreatments;
      const count = currentSelected
        .filter((i) => i.treatment_product_id === treatment.id && !i._destroy)
        .filter((i) => !i.id) // count only not yet persisted items
        .length;
      return (
        <MobileTreatmentRowWithCounter
          key={treatment.id}
          treatment={treatment}
          count={count}
          onAdd={() => onMobileTreatmentAdd(treatment)}
          onRemove={() => onMobileTreatmentRemove(treatment.id, currentSelected)}
          abfTracking={checkup.abf_tracking}
        />
      );
    }

    const currentSelectedProductId = selectedTreatments[selectedIndex]?.treatment_product_id;
    const selectedRow = mode === 'change' ? (currentSelectedProductId === treatment.id) : isSelected(treatment);
    const disabledRow = mode === 'change' ? (currentSelectedProductId === treatment.id) : isDisabled(treatment);
    const actionBtnType = mode === 'change' ? 'button' : 'checkbox';

    return (
      <MobileTreatmentRow
        key={treatment.id}
        treatment={treatment}
        actionBtnType={actionBtnType}
        disabledRow={disabledRow}
        onRowClick={onRowClick}
        selectedRow={selectedRow}
        abfTracking={checkup.abf_tracking}
      />
    );
  };

  const addRow = () => {
    if (!isMobile) {
      setSelectedData([...selectedTreatments, {}]);
      return;
    }

    openPortalRight(
      <MobileListPickerAllowDuplicates
        title={<FormattedMessage id="general.pageTitle.addMedications" />}
        mode="add"
        idKey="treatment_product_id"
        rightButtonOptions={{ label: 'next', disableOnEmptyList: true }}
        rowRenderer={renderMobileListRow()}
      />
    );
  };

  const removeRow = (index) => () => {
    if (selectedTreatments[index].id) {
      const destroyData = [];
      destroyData.push({ ...selectedTreatments[index], _destroy: true });
      setDestroyedData([...destroyedTreatments, ...destroyData]);
    }
    setSelectedData(update(selectedTreatments, { $splice: [[index, 1]] }));
  };

  const onTreatmentChange = (index, treatmentProduct) => {
    if (treatmentProduct) {
      const { id, vaccine, route, dosage_measure, dosage_type, safe } = treatmentProduct;
      const initialHeadCount = dosage_measure === 'head' && dosage_type === 'ml' ? 1 : checkup?.pigs || 0;
      let headCount;

      if (checkup.abf_tracking && !safe) {
        headCount = 0;
      } else if (vaccine || route.includes('oral')) {
        headCount = initialHeadCount;
      } else {
        headCount = 0;
      }

      // checking if treatment with added product was previously destroyed, if it was - mark it as undestroyed and use its values for added one
      const destroyedTreatment = destroyedTreatments.find(({ treatment_product_id }) => treatment_product_id === id);
      const isTreatmentPreviouslyDestroyed = !!destroyedTreatment;
      const newTreatment = isTreatmentPreviouslyDestroyed ?
        { ...destroyedTreatment, _destroy: undefined } :
        {
          id: !selectedTreatments.length ? undefined : selectedTreatments[index].id,
          treatment_product_id: parseInt(id, 10),
          skip_efficacy_survey: false
        };
      const overrideAttrs = {
        treatment_product: treatmentProduct,
        quantity: 0.00,
        head_count: headCount,
        ...(checkup.abf_tracking && !safe && { new_treatment: true })
      };
      // Check for matching future step
      const matchingFutureStep = findMatchingFutureStep(parseInt(id, 10));
      if (matchingFutureStep) {
        newTreatment.future_step = matchingFutureStep;
      }
      const newTreatments = update(selectedTreatments, { [index]: { $set: { ...newTreatment, ...overrideAttrs } } });
      setSelectedData(newTreatments);

      // if treatment with added product was previosly destroyed - remove it from destroyed list
      if (isTreatmentPreviouslyDestroyed) {
        setDestroyedData(destroyedTreatments.filter(({ treatment_product_id }) => (
          treatment_product_id !== destroyedTreatment.treatment_product_id)));
      }
    }
  };

  const addProtocolTreatment = (treatmentProtocolStep) => {
    const { id, quantity, gallons_value, treatment_protocol_product: { treatment_product } } = treatmentProtocolStep;
    const { vaccine, route, dosage_measure, dosage_type, safe } = treatment_product;

    // Correcting a day where the product was already recorded without the protocol link
    // links that record instead of adding a second one
    if (isEditMode) {
      const unlinkedIndex = selectedTreatments.findIndex((item) => (
        Number(item.treatment_product_id) === Number(treatment_product.id) &&
        !item.treatment_protocol_product_step_id &&
        !item._destroy &&
        item.useTreatment !== false
      ));
      if (unlinkedIndex !== -1) {
        setSelectedData(update(selectedTreatments, {
          [unlinkedIndex]: { $merge: { treatment_protocol_product_step_id: id, future_step: undefined } },
        }));
        return;
      }
    }

    const defaultValue = dosage_measure === 'head' && dosage_type === 'ml' ? 1 : checkup?.pigs || 0;
    let headCount;

    if (checkup.abf_tracking && !safe) {
      headCount = 0;
    } else if (vaccine || route.includes('oral')) {
      headCount = defaultValue;
    } else {
      headCount = 0;
    }
    const treatment = {
      treatment_product,
      treatment_product_id: parseInt(treatment_product.id, 10),
      treatment_protocol_product_step_id: id,
      skip_efficacy_survey: false,
      head_count: headCount,
      quantity: quantity != null ? parseFloat(quantity).toFixed(2) : '0.00',
      gallons_value: stockSolutionToSystem(parseFloat(gallons_value) || null, measurementSystem),
      useTreatment: true,
      ...(checkup.abf_tracking && !safe && { new_treatment: true })
    };
    const newTreatments = selectedTreatments.length === 1 && isEmpty(selectedTreatments[0])
      ? [treatment]
      : [...selectedTreatments, treatment];

    setSelectedData(newTreatments);
  };


  const parseDecimalText = (text, prev) => {
    const pattern = /^[+-]?\d+(\.\d+)?$/; // float number
    if (text === '') return text;
    if (!`0${text}0`.match(pattern)) return prev;
    return text;
  };

  const onDetailInputChange = (value, key, index) => {
    const { initial_pigs, pigs } = checkup;
    const pigsAmount = Math.max(initial_pigs, pigs) || 0;
    const prev = selectedTreatments[index]?.[key];
    let newValue = ['gallons_value'].includes(key)
      ? parseDecimalText(value, prev)
      : parseInt(value, 10);
    if (newValue !== prev) {
      if (key === 'head_count') {
        newValue = newValue <= pigsAmount ? newValue : pigsAmount;
      }
      const treatments = update(selectedTreatments, {
        [index]: { $merge: { [key]: parseFloat(newValue) } },
      });
      setSelectedData(treatments);
    }
  };

  const onUseTreatmentChange = (index, newValue) => {
    const { target: { value: useTreatment } } = newValue;
    const treatments = update(selectedTreatments, {
      [index]: { $merge: { useTreatment: JSON.parse(useTreatment) } },
    });
    setSelectedData(treatments);
  };

  const onNewTreatmentChange = (index, newValue) => {
    const { target: { value: new_treatment } } = newValue;
    const treatments = update(selectedTreatments, {
      [index]: { $merge: { new_treatment: JSON.parse(new_treatment) } },
    });
    setSelectedData(treatments);
  };

  const onUseFutureStepChange = (index, newValue, future_step) => {
    const { target: { value: useFutureStep } } = newValue;
    const { quantity, gallons_value } = future_step;
    const treatments = update(selectedTreatments, {
      [index]: { $merge: {
        useFutureStep: JSON.parse(useFutureStep),
        ...(JSON.parse(useFutureStep) && {
          quantity: quantity != null ? parseFloat(quantity).toFixed(2) : '0.00',
          gallons_value: stockSolutionToSystem(parseFloat(gallons_value) || null, measurementSystem)
        })
      } },
    });
    setSelectedData(treatments);
  };

  const optionRenderer = (option) => {
    const className = getMedType(option);

    return (
      <span className={`medication ${className} ${option.divider ? 'divider' : ''}`}>
        <span>
          <i className={`fa fa-${className}`} />
          {option.name}
        </span>
        <span className="right-text">
          {option.common && <FormattedMessage id="general.meds.common" />}
          {option.vaccine && <>&nbsp;(<FormattedMessage id="general.vaccine" />)</>}
          {checkup.abf_tracking && (
            <>
              {option.safe && <>&nbsp;(<FormattedMessage id="general.safe" />)</>}
              {!option.safe && <>&nbsp;(<FormattedMessage id="general.unsafe" />)</>}
            </>
          )}
        </span>
      </span>
    );
  };

  const onMobileModeChange = (index) => {
    setSelectedTreatmentIndex(index);
    openPortalRight(
      <MobileListPicker
        title={<FormattedMessage id="general.pageTitle.changeMedication" />}
        mode={selectedTreatments.length ? 'change' : 'add'}
        rightButtonOptions={selectedTreatments.length ? undefined : { label: 'next' }}
        idKey="treatment_product_id"
        rowRenderer={renderMobileListRow(index)}
      />
    );
  };

  useEffect(() => {

  }, [selectedTreatmentIndex]);

  const submitData = () => {
    // validateCheckup is declared in DailyCheckupContainer
    if (!validateCheckup(checkup)) {
      return;
    }

    const filteredList = selectedTreatments
      .filter((item) => item.treatment_product_id && item.useTreatment !== false)
      .map((item) => {
        const converted = { ...item, gallons_value: stockSolutionToStored(item.gallons_value, measurementSystem) };
        if (item.useFutureStep && item.future_step) {
          return { ...converted, treatment_protocol_product_step_id: item.future_step.id };
        }
        return converted;
      });
    const destroyedFilteredList = destroyedTreatments.filter(({ id, _destroy }) => !!id && !!_destroy);

    const unsafeTreatedPigs = checkup.abf_tracking
      ? selectedTreatments
        .filter((item) => item.useTreatment !== false)
        .filter(({ treatment_product, new_treatment }) => !treatment_product?.safe && new_treatment !== false)
        .map(({ head_count }) => head_count || 0)
        .reduce((prev, next) => prev + next, 0)
      : 0;

    const resource = {
      dc_treatments: [...filteredList, ...destroyedFilteredList],
      comments_treatments: { text: comment, mentioned_user_ids: mentionsIds },
      total_pigs_treated: headCount,
      unsafe_pigs_treated: unsafeTreatedPigs > checkup.pigs ? checkup.pigs : unsafeTreatedPigs,
    };

    const { checkupUrl } = getCheckupBackUrls(checkup, location);

    const submitRequest = isEditMode
      ? makeCheckupCorrection(checkup.id, resource)
      : reportTreatments(checkup.id, { resource });

    submitRequest
      .then(() => router.push(checkupUrl))
      .catch((response) => {
        sendNotify(response, currentUser);
        return toastResponseErrors(response);
      });
  };

  const validateHeadCount = (headCountArr, value) => {
    const { initial_pigs, pigs } = checkup;
    const pigsAmount = Math.max(initial_pigs, pigs) || 0;

    if (headCountArr.length === 1) return headCountArr[0];

    const maxHeadTreatment = Math.max(...headCountArr);

    if (value < maxHeadTreatment) return maxHeadTreatment;
    if (value > pigsAmount) return pigsAmount;
    return value;
  };

  const handleSetHeadCount = (val) => {
    const value = Number.isInteger(val) ? val : parseInt(val.target.value, 10);
    const input = document.querySelector('#head-total-count');
    const isFocused = (document.activeElement === input);
    const headCountArr = buildHeadsCountData(selectedTreatments);

    if (!headCountArr.length) return;

    const headCountValue = isFocused && headCountArr.length > 1
      ? value
      : validateHeadCount(headCountArr, value);

    if (headCount !== headCountValue) setHeadCount(headCountValue);
  };

  const setHeadCountBlur = (e) => {
    const value = parseInt(e.target.value, 10);
    const headCountArr = buildHeadsCountData(selectedTreatments);

    if (!headCountArr.length) return;

    setHeadCount(validateHeadCount(headCountArr, value));
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  const onCommentOpen = () => {
    animateScroll.scrollToBottom({ containerId: 'bodybag' });
  };

  const handleBlur = ({ target: { value } }, key, index) => {
    const { pigs, initial_pigs } = checkup;
    const pigsAmount = Math.max(pigs, initial_pigs) || 0;
    const prev = selectedTreatments[index]?.[key];
    let newValue = parseInt(value, 10);
    if (newValue !== prev) {
      if (key === 'head_count') newValue = newValue <= pigsAmount ? newValue : pigsAmount;
      const treatments = update(selectedTreatments, {
        [index]: { $merge: { [key]: parseFloat(newValue) } },
      });
      setSelectedData(treatments);
    }
  };

  const onTreatmentQuantityChange = (index, value) => {
    const prev = selectedTreatments[index]?.quantity;
    const newValue = parseDecimalText(value, prev);
    if (newValue !== prev) {
      const treatments = update(selectedTreatments, { [index]: { $merge: { quantity: newValue } } });
      setSelectedData(treatments);
    }
  };

  const onTreatmentQuantityBlur = ({ target: { value } }, index) => {
    const currentValue = selectedTreatments[index]?.quantity;
    const newValue = Number.isInteger(+value) ? +value : parseFloat(value).toFixed(2);
    if (newValue !== currentValue) {
      const treatments = update(selectedTreatments, {
        [index]: { $merge: { quantity: Number.isNaN(+newValue) ? '' : newValue } }
      });
      setSelectedData(treatments);
    }
  };

  const needWithdrawalWarningMessage = (treatmentProduct) => {
    const { value, period } = withdrawalParams(treatmentProduct);
    const isWithdrawalWarnActual = moment(checkup.created_on).add(value, period) >= moment();

    return withdrawalTrackingOn && isWithdrawalWarnActual;
  };

  const getTreatmentLabel = (treatmentProduct) => {
    if (!treatmentProduct) return null;
    const { route, dosage_measure } = treatmentProduct;
    const dosage_type = formatDosageType(treatmentProduct.dosage_type, measurementSystem);
    const type = <FormattedMessage id={`general.short.${dosage_type}`} />;

    if (route.includes('oral')) {
      const msgKey = dosage_measure === 'head' ? 'oralLabel' : 'unitsAdministered';
      return (
        <FormattedMessage id={`container.checkupEdit.reportMedications.${msgKey}`} values={{ type }} />
      );
    }

    return (
      <FormattedMessage
        id="container.checkupEdit.reportMedications.injectableLabel"
        values={{ type, measure: <FormattedMessage id={`general.${dosage_measure}`} /> }}
      />
    );
  };

  const handleDeleteAsset = (assetId) => {
    if (!validateCheckup(checkup)) return;
    deleteAsset(checkup.id, assetId).catch(toastResponseErrors);
  };

  const isContinueDisabled = useMemo(() => {
    if (selectedTreatments.length === 0) {
      return !checkup.treatment_count;
    }

    const pigsAmount = Math.max(checkup?.initial_pigs, checkup?.pigs) || 0;

    return selectedTreatments.filter((item) => item.useTreatment !== false).some(({
      gallons_value,
      head_count,
      quantity,
      treatment_product_id,
      treatment_product,
      future_step,
      useFutureStep,
    }) => (
      !treatment_product_id ||
        !head_count ||
        (head_count > pigsAmount) ||
        ((treatment_product.dosage_measure === 'gal') && !gallons_value) ||
        ((getMedType(treatment_product) !== 'topical') && !parseFloat(quantity)) ||
        (future_step && (useFutureStep === undefined || useFutureStep === null))
    ));
  }, [selectedTreatments, checkup]);
  const hideTrash = (selectedTreatments.length === 1 && !selectedTreatments[0].treatment_product_id) ||
    !selectedTreatments.length;
  const { prevPageUrl } = getCheckupBackUrls(checkup, location);

  const clamp = (val, min, max) => Math.max(min, Math.min(val, max));

  const oldUnsafeTreatedPigs = checkup.abf_tracking
    ? (checkup.dc_treatments || [])
      .filter(({ treatment_product, new_treatment }) => !treatment_product?.safe && new_treatment !== false)
      .reduce((acc, { head_count }) => acc + (head_count || 0), 0)
    : 0;

  const newUnsafeTreatedPigs = checkup.abf_tracking
    ? selectedTreatments
      .filter(({ treatment_product, useTreatment, new_treatment }) => (
        treatment_product &&
        useTreatment !== false &&
        !treatment_product.safe &&
        new_treatment !== false
      ))
      .reduce((acc, { head_count }) => acc + (head_count || 0), 0)
    : 0;

  const base = clamp(checkup.treated_pigs - oldUnsafeTreatedPigs, 0, Number.MAX_SAFE_INTEGER);
  const initialTreatedFloor = checkup.initial_treated_pigs ?? 0;
  const totalTreatedPigs = clamp(base + newUnsafeTreatedPigs, initialTreatedFloor, checkup.pigs);

  return (
    <CenterBox
      isLoading={isReportingTreatments}
      renderIf={Boolean(checkup)}
      className="DailyCheckupReportTreatments"
    >
      <DailyCheckupEditProtector pigGroup={checkup.pig_group}>
        <section className="center-box-header show-for-large">
          <div>
            <div className="subtitle">{checkup.pig_group_name}</div>
            <h1 className="title"><FormattedMessage id="general.medications" /></h1>
          </div>
          <Link to={prevPageUrl}><i className="fa fa-times close-center-box" /></Link>
        </section>
        <PigsUnderCareLine mobile pigsCount={checkup.pigs} />
        {checkup.abf_tracking && <PigsUnderTreatmentLine mobile pigsCount={totalTreatedPigs} />}
        <section className="center-box-body">
          {isEditMode && (
            <MessageBox type="warning">
              <strong><FormattedMessage id="component.alertBox.warning" />&nbsp;</strong>
              <FormattedMessage id="container.checkupEdit.editWarning" />
            </MessageBox>
          )}
          {isOnline && (
            <TreatmentProtocolsProductSteps
              checkupId={checkup.id}
              date={checkup.created_on}
              isEditMode={isEditMode}
              pigGroup={checkup.pig_group}
              selectedStepIds={selectedTreatments
                .map((treatment) => treatment.treatment_protocol_product_step_id)
                .filter(Boolean)
              }
              onReport={addProtocolTreatment}
            />
          )}
          <div className="treatment-rows">
            {(selectedTreatments.length ? selectedTreatments : [{}]).map((treatment, index) => {
              const {
                treatment_product_id,
                treatment_product,
                head_count,
                quantity,
                gallons_value,
                treatment_protocol_product_step_id,
                useTreatment,
                future_step,
                useFutureStep,
                new_treatment,
              } = treatment;

              const isTreatmentProtocolProduct = !!treatment_protocol_product_step_id;

              return (
                <div
                  key={`treatment-${index}-${treatment_product_id}`}
                  className={cn('treatment-row', { 'disabled': isTreatmentProtocolProduct })}
                >
                  <div className="select-treatment-line">
                    <div className="index-cell">
                      <div className="row-index">{index + 1}</div>
                    </div>
                    <div className="select-treatment-wrapper">
                      {isMobile ? (
                        <div
                          className="mobile-treatment-input"
                          onClick={isTreatmentProtocolProduct ? undefined : () => onMobileModeChange(index)}
                        >
                          <div className="treatment-name">
                            {treatment_product?.name || (
                            <span className={cn('placeholder')}>
                              <FormattedMessage id="general.pageTitle.selectMedications" />
                            </span>
                              )}
                          </div>
                          <i className="change-treatment-icon fa fa-angle-right-btb" />
                        </div>
                      ) : (
                        <ReactSelect
                          placeholder={<FormattedMessage id="general.pageTitle.selectMedications" />}
                          options={treatmentsList}
                          valueKey="id"
                          labelKey="name"
                          arrowRenderer={() => <i className="fa fa-angle-down" />}
                          value={treatment_product_id}
                          onChange={(value) => onTreatmentChange(index, value)}
                          resetValue={{}}
                          disabled={isTreatmentProtocolProduct}
                          clearable={false}
                          optionRenderer={optionRenderer}
                        />
                      )}
                    </div>
                    <div
                      className={cn('trash-cell', {
                        'hidden': hideTrash || (isTreatmentProtocolProduct && (!treatment.id || !isAdmin))
                      })}
                    >
                      <i className="fa fa-trash-o trash-icon" onClick={removeRow(index)} />
                    </div>
                  </div>

                  {isTreatmentProtocolProduct && (
                    <MessageBox type="default" icon={<i className="fa fa-info-circle" />}>
                      <FormattedMessage id="general.productIsAppliedAccordingToTreatmentProtocol" />
                    </MessageBox>
                  )}
                  {!isEditMode && future_step && (
                    <MessageBox type="default" icon={<i className="fa fa-info-circle" />}>
                      <FormattedMessage id="general.productFutureTreatmentProtocol" />
                    </MessageBox>
                  )}

                  {treatment_product_id && needWithdrawalWarningMessage(treatment_product)
                  && (<WithdrawalWarningBox treatmentProduct={treatment_product} checkup={checkup} />)}
                  {treatment_product_id && treatment_product.dosage_type === 'unit' && (
                    <AttentionBox className="oral-attention">
                      <FormattedMessage id="general.unitSize" />&nbsp;•&nbsp;
                      <OralTreatmentUnit product={treatment_product} />
                    </AttentionBox>
                  )}
                  {treatment_product_id && treatment_product.notes && (
                    <AttentionBox showIcon={false} className="oral-attention">
                      <span>
                        <b><FormattedMessage id="general.notes" /> •&nbsp;</b>
                        <Translation text={treatment_product.notes}>
                          {treatment_product.notes}
                        </Translation>
                      </span>
                    </AttentionBox>
                  )}
                  {treatment_product_id && (
                    <div
                      className={cn(
                        'select-values-line',
                        { 'skip-margin-bottom': treatment_product.efficacy_survey && !isEditMode }
                      )}
                    >
                      <div className="index-cell" />
                      <div className="treatment-details">
                        <div className="treatment-details-row">
                          <i className="fa fa-exclamation-triangle-bts icon" />
                          <NumberStepInput
                            className="treatment-number-input"
                            label={<FormattedMessage id="container.checkupEdit.reportMedications.headTreated" />}
                            value={head_count || ''}
                            onChange={(value) => {
                              onDetailInputChange(value, 'head_count', index);
                            }}
                            onFocus={handleFocus}
                            onBlur={(e) => handleBlur(e, 'head_count', index)}
                            isDisabled={isTreatmentProtocolProduct && !treatment.id && !useTreatment}
                          />
                        </div>
                        {!treatment_product.route.includes('topical') && (
                          <div className="treatment-details-row">
                            <i
                              className={cn(
                                'fa fa-exclamation-triangle-bts icon',
                                { 'visible': treatment_product.vaccine && (quantity === 0) },
                              )}
                            />
                            <NumberStepInput
                              label={getTreatmentLabel(treatment_product)}
                              value={quantity || 0}
                              onChange={(value) => onTreatmentQuantityChange(index, value)}
                              onFocus={handleFocus}
                              onBlur={(e) => onTreatmentQuantityBlur(e, index)}
                              hasWarning={treatment_product.vaccine && (quantity === 0)}
                              className="treatment-number-input"
                              step={isMobile ? 0.1 : 0.25}
                              valueParser={(val) => val}
                              normalizeValue={(value) => value.toString()}
                              isDisabled={
                                (isTreatmentProtocolProduct || useFutureStep) &&
                                !isAdmin &&
                                (treatment_product.dosage_measure !== 'gal' ||
                                  treatment_product.route.includes('topical'))
                              }
                            />
                          </div>
                        )}
                        {treatment_product.dosage_measure === 'gal' &&
                        !treatment_product.route.includes('topical') && (
                          <div className="treatment-details-row">
                            <i
                              className={cn(
                                'fa fa-exclamation-triangle-bts icon',
                                { 'visible': treatment_product.vaccine && (quantity === 0) },
                              )}
                            />
                            <NumberStepInput
                              className="treatment-number-input"
                              label={(
                                <FormattedMessage
                                  id={measurementSystem === 'imperial' ? 'general.gallonsWater' : 'general.litersWater'}
                                />
                              )}
                              value={gallons_value || ''}
                              onChange={(value) => onDetailInputChange(value, 'gallons_value', index)}
                              onFocus={handleFocus}
                              onBlur={(e) => handleBlur(e, 'gallons_value', index)}
                            />
                          </div>
                        )}
                        {checkup.abf_tracking && !treatment_product?.safe && (
                          <div className="treatment-details-row">
                            <i className="fa fa-exclamation-triangle-bts icon" />
                            <div className={cn('useTreatmentInput')}>
                              <label className={cn('treatmentInputLabel')}>
                                <div className="label-wrapper">
                                  <span><FormattedMessage id="general.treatmentType" /></span>
                                </div>
                                <div className={cn('brg-field')}>
                                  <ButtonRadioGroup
                                    name={`newTreatment-${index}`}
                                    className="radio-button-group"
                                    options={newTreatmentOptions}
                                    value={new_treatment}
                                    onChange={(newValue) => onNewTreatmentChange(index, newValue)}
                                  />
                                </div>
                              </label>
                            </div>
                          </div>
                        )}
                        {isTreatmentProtocolProduct && !treatment.id && (
                          <div className="treatment-details-row">
                            <i className="fa fa-exclamation-triangle-bts icon" />
                            <div className={cn('useTreatmentInput')}>
                              <label className={cn('treatmentInputLabel')}>
                                <div className="label-wrapper">
                                  <span><FormattedMessage id="general.useTreatment" /></span>
                                </div>
                                <div className={cn('brg-field')}>
                                  <ButtonRadioGroup
                                    name={`useTreatment-${index}`}
                                    className="radio-button-group"
                                    options={useTreatmentOptions}
                                    value={useTreatment}
                                    onChange={(newValue) => onUseTreatmentChange(index, newValue)}
                                  />
                                </div>
                              </label>
                            </div>
                          </div>
                        )}
                        {!isEditMode && future_step && !treatment.id && (
                          <div className="treatment-details-row">
                            <i
                              className={cn(
                                'fa fa-exclamation-triangle-bts icon',
                                { visible: useFutureStep === undefined },
                              )}
                            />
                            <div className={cn('useTreatmentInput')}>
                              <label className={cn('treatmentInputLabel')}>
                                <div className="label-wrapper">
                                  <span><FormattedMessage id="general.useFutureStep" /></span>
                                </div>
                                <div className={cn('brg-field')}>
                                  <ButtonRadioGroup
                                    name={`useFutureStep-${index}`}
                                    className="radio-button-group"
                                    options={useTreatmentOptions}
                                    value={useFutureStep}
                                    onChange={(newValue) => onUseFutureStepChange(index, newValue, future_step)}
                                  />
                                </div>
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className={cn('trash-cell', { 'hidden': hideTrash })} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!!selectedTreatments.length && (
            <button className="add-treatment" onClick={addRow}>
              <FormattedMessage id="container.checkupEdit.reportMedications.add" />
            </button>
          )}

          <CommentBox
            hasMentions={hasMentions}
            disabledIds={mentionsIds}
            entityId={checkup?.pig_group?.farm_id}
            entityType="farm"
            onOpen={onCommentOpen}
            onValueChange={setComment}
            onMentionsChange={(mentions) => { setMentionsIds(mentions.map((i) => i.id)); }}
            value={comment}
            isOpen={!!comment}
            placeholder=""
            useSpeechToText
          />

          <div className="treatment-details">
            <div className="treatment-details-row total-count">
              <i className="fa fa-exclamation-triangle-bts icon visible" />
              <NumberStepInput
                label={<FormattedMessage id="container.checkupEdit.reportMedications.totalTreated" />}
                value={headCount}
                onChange={handleSetHeadCount}
                onFocus={handleFocus}
                onBlur={setHeadCountBlur}
                hasWarning
                className="treatment-number-input always-show-warning"
                id="head-total-count"
              />
            </div>
          </div>

          {+params.id === checkup?.id && media_upload && (
            <MediaUploader
              checkup={checkup}
              customUploadPath={`${API_URL}/daily_checkups/${params.id}/chunk?category=treatments`}
              deleteAsset={handleDeleteAsset}
              filterAssets={(asset) => asset.category === 'treatments'}
              headerLess
              openViewer={openViewer}
              paramsCheckupId={params.id}
              uploadAssetComplete={uploadAssetComplete}
              validateCheckup={validateCheckup}
            />
          )}

        </section>

        <section className="center-box-footer show-for-large">
          <div className="buttons text-right">
            <Link className="button margin-right-1" to={prevPageUrl}>
              <FormattedMessage id="general.button.cancel" />
            </Link>
            <Button
              primary
              disabled={isContinueDisabled || isReportingTreatments}
              className="submit wider"
              onClick={submitData}
            >
              <FormattedMessage id="general.button.continue" />
            </Button>
          </div>
        </section>

        <StickyFooter className="hide-for-large" hidden={isContinueDisabled}>
          <Button
            stickyFooter
            primary
            disabled={isContinueDisabled || isReportingTreatments}
            onClick={submitData}
          >
            <FormattedMessage id="general.button.continue" />
          </Button>
        </StickyFooter>
      </DailyCheckupEditProtector>
    </CenterBox>
  );
};

DailyCheckupReportTreatments.contextTypes = {
  router: T.object.isRequired,
};

DailyCheckupReportTreatments.propTypes = {
  checkup: T.object,
  closePortalRight: T.func.isRequired,
  currentUser: T.object,
  deleteAsset: T.func.isRequired,
  destroyedTreatments: T.array.isRequired,
  fetchTreatmentsList: T.func.isRequired,
  isEditMode: T.bool,
  hasMentions: T.bool,
  isOnline: T.bool.isRequired,
  isReportingTreatments: T.bool.isRequired,
  location: T.object.isRequired,
  makeCheckupCorrection: T.func,
  measurementSystem: T.string,
  openPortalRight: T.func.isRequired,
  openViewer: T.func.isRequired,
  params: T.object.isRequired,
  reportTreatments: T.func.isRequired,
  resetTreatmentsData: T.func.isRequired,
  selectedTreatments: T.array.isRequired,
  selectTreatment: T.func.isRequired,
  setDestroyedData: T.func.isRequired,
  setPageOptions: T.func,
  setSelectedData: T.func.isRequired,
  treatmentsList: T.array.isRequired,
  uploadAssetComplete: T.func.isRequired,
  validateCheckup: T.func.isRequired,
  withdrawalTrackingOn: T.bool,
};

export default compose(
  connect((state, props) => ({
    currentUser: state.auth.user,
    destroyedTreatments: state.mobileListPicker.destroyed,
    isOnline: state.network.isOnline,
    isReportingTreatments: props.isLoading || state.dailyCheckup.checkupEdit.isReportingTreatments,
    measurementSystem: state.auth.user.current_company.measurement_system,
    selectedTreatments: state.mobileListPicker.selected,
    treatmentsList: state.mobileListPicker.dataList.resources,
    withdrawalTrackingOn: state.auth.user.current_company.withdrawal_tracking_on,
    hasMentions: !props.isEditMode || state.auth.user.current_company.edit_mode_mentions,
  }), {
    closePortalRight,
    deleteAsset,
    fetchTreatmentsList,
    openPortalRight,
    openViewer,
    reportTreatments,
    resetTreatmentsData,
    selectTreatment,
    setDestroyedData,
    setPageOptions,
    setSelectedData,
    uploadAssetComplete,
  }),
  withCorrection,
)(DailyCheckupReportTreatments);
